import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api/auth';
import { AccountDropdown } from '../AccountDropdown';
import { MobileNav } from '../MobileNav';
import { useBreakpoint, useShowAuthenticatedLayout } from '@/hooks';
// Animated Flouri icon with flowing petal effect
// Center point where petals connect: approximately (300, 420)
const AnimatedFlouriIcon = () => (
  <svg
    viewBox="0 0 700 700"
    className="w-6 h-6"
    style={{ overflow: 'visible' }}
    aria-hidden="true"
  >
    {/* Top-left petal - rotates around center point (shimmer arrives last) */}
    <path fill="#fff" d="M251.9,383.35v-92.47c0-59.4-48.15-107.55-107.55-107.55h0c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z">
      <animate attributeName="fill" values="#fff;#fff;#ee3962;#fff" keyTimes="0;0.86;0.95;1" dur="15s" repeatCount="indefinite" />
      <animateTransform attributeName="transform" type="rotate" values="0 300 420;0 300 420;-8 300 420;2.5 300 420;0 300 420" keyTimes="0;0.86;0.91;0.97;1" dur="15s" repeatCount="indefinite" />
    </path>
    {/* Bottom-left petal (shimmer arrives second to last) */}
    <path fill="#fff" d="M236.82,449.42h-92.47c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-92.47c0-8.33-6.75-15.08-15.08-15.08Z">
      <animate attributeName="fill" values="#fff;#fff;#ee3962;#fff" keyTimes="0;0.845;0.935;1" dur="15s" repeatCount="indefinite" />
      <animateTransform attributeName="transform" type="rotate" values="0 300 420;0 300 420;8 300 420;-2 300 420;0 300 420" keyTimes="0;0.845;0.895;0.96;1" dur="15s" repeatCount="indefinite" />
    </path>
    {/* Large top-right petal - outer shape (stationary, shimmer arrives second) */}
    <path fill="#fff" d="M488.92,26.88h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87c0-102.36-82.98-185.34-185.34-185.34Z">
      <animate attributeName="fill" values="#fff;#fff;#ee3962;#fff" keyTimes="0;0.835;0.925;1" dur="15s" repeatCount="indefinite" />
    </path>
    {/* Large top-right petal - inner cutout (rotates counter-clockwise around main center) */}
    <path fill="#373636" d="M526.96,261.37c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z">
      <animate attributeName="fill" values="#373636;#373636;#2a2929;#373636" keyTimes="0;0.835;0.925;1" dur="15s" repeatCount="indefinite" />
      <animateTransform attributeName="transform" type="rotate" values="0 300 420;0 300 420;-3 300 420;1.5 300 420;0 300 420" keyTimes="0;0.835;0.885;0.94;1" dur="15s" repeatCount="indefinite" />
    </path>
    {/* Bottom-right petal (clockwise rotation, shimmer arrives first) */}
    <path fill="#fff" d="M302.71,465v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z">
      <animate attributeName="fill" values="#fff;#fff;#ee3962;#fff" keyTimes="0;0.83;0.92;1" dur="15s" repeatCount="indefinite" />
      <animateTransform attributeName="transform" type="rotate" values="0 300 420;0 300 420;15 300 420;-5 300 420;0 300 420" keyTimes="0;0.83;0.88;0.94;1" dur="15s" repeatCount="indefinite" />
    </path>
  </svg>
);

export function Header() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const showAuthenticatedLayout = useShowAuthenticatedLayout();

  // Close mobile menu when navigating to a new page
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Fetch profile data to get company name
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => authApi.getUserProfile(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const companyName = profile?.companyName;

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';

  // Mobile Header
  if (isMobile) {
    return (
      <>
        <header className="fixed top-0 left-0 right-0 bg-flouri-darkGray border-b border-gray-800 h-[var(--header-h)] z-50">
          <div className="px-4 h-full flex items-center justify-between">
            {/* Left: Icon always visible + Company Name when authenticated */}
            <Link
              to={isAuthenticated ? '/dashboard' : '/'}
              reloadDocument={!isAuthenticated}
              aria-label={isAuthenticated && companyName ? `${companyName} Workspace home` : 'Flouri home'}
              className="flex items-center space-x-2 min-w-0 flex-1"
            >
              <AnimatedFlouriIcon />
              {isAuthenticated && companyName && (
                <span className="text-white truncate">
                  <span className="text-lg font-bold">{companyName}</span>{' '}
                  <span className="text-base font-medium">Workspace</span>
                </span>
              )}
            </Link>

            {/* Right: Hamburger or Sign In */}
            {isAuthenticated ? (
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors ml-2"
                aria-label="Open menu"
                aria-expanded={isMobileMenuOpen}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            ) : showAuthenticatedLayout ? null : (
              <Link
                to="/login"
                className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-md hover:bg-primary-600 transition-colors ml-2"
              >
                Sign In
              </Link>
            )}
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        {isAuthenticated && (
          <MobileNav
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            userName={displayName}
            companyName={companyName}
            email={user?.email}
          />
        )}
      </>
    );
  }

  // Desktop Header - persistent top bar with company name + account dropdown.
  // Primary navigation lives in the left Sidebar (see Layout).
  return (
    <header className="fixed top-0 left-0 right-0 bg-flouri-darkGray h-[var(--header-h)] z-50">
      <div className="h-full pl-5 pr-4 sm:pr-6 lg:pr-8 flex items-center justify-between">
        <Link
          to={isAuthenticated ? '/dashboard' : '/'}
          reloadDocument={!isAuthenticated}
          aria-label={isAuthenticated && companyName ? `${companyName} Workspace home` : 'Flouri home'}
          className="flex items-center space-x-6 min-w-0"
        >
          <span className="translate-x-0.5">
            <AnimatedFlouriIcon />
          </span>
          {isAuthenticated && companyName && (
            <span className="text-white truncate">
              <span className="text-xl font-bold">{companyName}</span>{' '}
              <span className="text-lg font-medium">Workspace</span>
            </span>
          )}
        </Link>

        {isAuthenticated ? (
          <AccountDropdown />
        ) : showAuthenticatedLayout ? null : (
          <Link
            to="/login"
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            Sign In
          </Link>
        )}
      </div>
    </header>
  );
}
